<script setup>
import { ref, reactive, computed } from "vue";
import emailjs from "@emailjs/browser";
import useVuelidate from '@vuelidate/core';
import { required, email, minLength, helpers } from '@vuelidate/validators';

const state = reactive({
  name: "",
  email: "",
  employees: null,
  telephone: "",
  location: "",
  activities: "",
  message: "",
});

const formFields = ref([
    { id: 'fullname', type: 'text', model: 'name', name: 'name' },
    { id: 'email', type: 'email', model: 'email', name: 'email' },
    { id: 'phone', type: 'tel', model: 'telephone', name: 'telephone' },
    { id: 'location', type: 'text', model: 'location', name: 'location' },
    { id: 'Activities', type: 'text', model: 'activities', name: 'activities' },
]);

const phoneNumberValidator = helpers.withMessage(
    'Invalid phone number format',
    helpers.regex(/^(\+?[1-9]{1,4}[-.\s]?)?(\(?\d{1,4}\)?[-.\s]?)?[\d\s.-]{5,15}$/)
);

const rules = computed(() => ({
  name: { required },
  email: { required, email },
  employees: { required },
  telephone: { required, phoneNumberValidator },
  location: { required },
  activities: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(20) },
}));

const v$ = useVuelidate(rules, state);
const isSubmitting = ref(false);

async function submitForm() {
  const isFormCorrect = await v$.value.$validate();
  if (!isFormCorrect) {
    console.log("Problem when submitting", v$.value.$errors);
    return;
  }

  isSubmitting.value = true;
  console.log("Submitting form...", state);

  try {
    const result = await emailjs.sendForm('service_x0vrhv2', 'template_sfdbbod', '#myForm', 'polQPG6B-ulsOOaj4');
    alert('Email Sent!', result.text);
    // Optionally reset form
    // Object.assign(state, { name: "", email: "", ... });
    // v$.value.$reset();
  } catch (error) {
    console.log('Email was not sent', error);
    alert('Email was not sent. Please try again.');
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="page-container">
    <div class="form-wrapper">
      <div class="left-panel">
        <div class="overlay"></div>
        <div class="panel-content">
          <h1>{{ $t('partnerShip-Page.left-side') }}</h1>
          <p>{{ $t('Join us to build the future of logistics together.') }}</p>
        </div>
      </div>
      <div class="right-panel">
        <form id="myForm" @submit.prevent="submitForm">
          <div v-for="(field, index) in formFields" :key="field.id" class="form-group">
            <label :for="field.id">{{ $t(`partnerShip-Page.fields[${index}].title`) }}</label>
            <input :type="field.type" :id="field.id" :placeholder="$t(`partnerShip-Page.fields[${index}].placeholder`)" v-model="state[field.model]" :name="field.name" :class="{ 'input-error': v$[field.model].$error }">
            <div class="error-message" v-if="v$[field.model].$error">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="error-icon"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" /></svg>
              <span>{{ v$.value[field.model].$errors[0].$message }}</span>
            </div>
          </div>

          <div class="form-group">
            <label for="message">{{ $t('partnerShip-Page.fields[5].title') }}</label>
            <textarea id="message" :placeholder="$t('partnerShip-Page.fields[5].placeholder')" v-model="state.message" name="message" :class="{ 'input-error': v$.message.$error }"></textarea>
            <div class="error-message" v-if="v$.message.$error">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="error-icon"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" /></svg>
              <span>{{ v$.message.$errors[0].$message }}</span>
            </div>
          </div>

          <div class="form-group">
            <label>{{ $t('partnerShip-Page.fields[6].title') }}?</label>
            <div class="business-size">
              <label v-for="size in ['1-50', '51-150', '151-500', '500+']" :key="size" :class="{ 'radio-checked': state.employees === size }">
                <input type="radio" v-model="state.employees" :value="size" name="employees">
                <span>{{ size }}</span>
              </label>
            </div>
            <div class="error-message" v-if="v$.employees.$error">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="error-icon"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" /></svg>
              <span>{{ v$.employees.$errors[0].$message }}</span>
            </div>
          </div>

          <button type="submit" class="submit-btn" :disabled="isSubmitting">
            <span v-if="isSubmitting">{{ $t('partnerShip-Page.submitting', 'Submitting...') }}</span>
            <span v-else>{{ $t('partnerShip-Page.submit') }}</span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<style  scoped>
.page-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f0f2f5;
  padding: 100px 20px;
}

.form-wrapper {
    width: 100%;
    max-width: 1200px;
    display: flex;
    background-color: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.left-panel{
  background: url("../assets/partners.jpg") center center no-repeat;
  background-size: cover;
  position: relative;
  color: white;
}
.overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(10, 45, 97, 0.7); /* Primary blue overlay */
    z-index: 1;
  }
.panel-content {
  position: relative;
  z-index: 2;
}
.left-panel, .right-panel {
    padding: 50px;
    flex: 1;
}

.left-panel {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
}

.left-panel h1 {
  font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
}

.left-panel p {
  font-size: 1.1rem;
  max-width: 400px;
  line-height: 1.6;
  opacity: 0.9;
}

.right-panel {
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.form-group {
  margin-bottom: 1.5rem;
}

label {
  font-size: 0.9rem;
  color: #333;
  margin-bottom: 8px;
  font-weight: 600;
  display: block;
}

input[type="text"],
input[type="email"],
input[type="tel"],
textarea {
  width: 100%;
  padding: 14px;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

input:focus, textarea:focus {
  outline: none;
  border-color: #0A2D61;
  box-shadow: 0 0 0 3px rgba(10, 45, 97, 0.1);
}

textarea {
  min-height: 120px;
  resize: vertical;
}

.input-error {
  border-color: #e53e3e;
}

.error-message {
  display: flex;
  align-items: center;
  color: #e53e3e;
  font-size: 0.875rem;
  margin-top: 8px;
}
.error-icon {
  width: 16px;
  height: 16px;
  margin-right: 6px;
}

.business-size {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.business-size label {
  padding: 10px 20px;
  border: 1px solid #dcdcdc;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.business-size label:hover {
  background-color: #f0f2f5;
}

.business-size input[type="radio"] {
  display: none;
}

.business-size .radio-checked {
  background-color: #0A2D61;
  color: white;
  border-color: #0A2D61;
}

.submit-btn {
    background-color: #0A2D61;
    color: white;
    padding: 16px 20px;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.3s ease, transform 0.2s ease;
    width: 100%;
    margin-top: 1rem;
}

.submit-btn:hover {
    background-color: #1c4b8d;
    transform: translateY(-2px);
}

.submit-btn:disabled {
  background-color: #a0aec0;
  cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 768px) {
  .form-wrapper {
    flex-direction: column;
  }
  .left-panel {
    min-height: 300px;
  }
}

</style>